"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import cn from "classnames"
import useEmblaCarousel from "embla-carousel-react"

import { Asset } from "~/components/primitives/Asset/Asset"

import type { ArticleCarouselSlide } from "./ArticleCarousel.interface"
import s from "./ArticleCarousel.module.scss"

type Props = {
  slides: ArticleCarouselSlide[]
}

export const ArticleCarouselView = ({ slides }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isFocusWithin, setIsFocusWithin] = useState(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
    skipSnaps: false,
  })

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      if (wrapperRef.current?.contains(e.target as Node | null)) {
        setIsFocusWithin(true)
      }
    }
    const onFocusOut = (e: FocusEvent) => {
      if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) {
        setIsFocusWithin(false)
      }
    }
    document.addEventListener("focusin", onFocusIn)
    document.addEventListener("focusout", onFocusOut)
    return () => {
      document.removeEventListener("focusin", onFocusIn)
      document.removeEventListener("focusout", onFocusOut)
    }
  }, [wrapperRef])

  useEffect(() => {
    if (!isFocusWithin) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        scrollPrev()
      } else if (e.key === "ArrowRight") {
        scrollNext()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isFocusWithin, scrollNext, scrollPrev])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
    setCurrentIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)
    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <div ref={wrapperRef} className={s.wrapper}>
      <div className={s.viewport} ref={emblaRef}>
        <div className={s.container}>
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={s.slide}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${slides.length}`}
            >
              <div className={s.imageWrap}>
                <Asset
                  className={s.image}
                  asset={slide.image}
                  imageSizes={{ base: "100vw", lg: "80vw" }}
                  objectFit="cover"
                  hasPriority={index === 0}
                />
              </div>
              {slide.caption && <p className={s.caption}>{slide.caption}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className={cn(s.controlsContainer, "container", "default-grid")}>
        <div className={s.controls}>
          <button
            className={s.arrow}
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            aria-label="Previous slide"
            type="button"
          >
            <svg
              viewBox="0 0 23 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2874_4252)">
                <path
                  d="M21.1275 9.96766L1.7675 9.96766"
                  stroke="#121212"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M9.96735 18.1649L1.76788 9.96485L9.96734 1.766"
                  stroke="#121212"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </g>
            </svg>
          </button>
          <span className={s.counter} aria-live="polite">
            {currentIndex + 1} / {slides.length}
          </span>
          <button
            className={s.arrow}
            onClick={scrollNext}
            disabled={!canScrollNext}
            aria-label="Next slide"
            type="button"
          >
            <svg
              viewBox="0 0 23 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2874_4258)">
                <path
                  d="M1.25 9.96484H20.61"
                  stroke="#121212"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M12.4102 1.76562L20.6096 9.9657L12.4102 18.1646"
                  stroke="#121212"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </g>
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
